import { useEffect, useState, useContext } from 'react'
import PropTypes from 'prop-types'
import { connect, useSelector } from 'react-redux'
import { push } from 'connected-react-router'
import { Prompt } from 'react-router-dom'
import { setUserNeedsAppUpdate } from 'actions/appupdate'
import RefreshApp from 'components/App/RefreshApp'
import Announcements from 'components/Student/Announcements/Index'
import SideNav from 'components/App/SideNav'
import Footer from 'components/App/Footer'
import useConfigureRollbar from 'hooks/useConfigureRollbar'
import useMentorGlobalPusherSubscriptions from 'hooks/Pusher/useMentorGlobalPusherSubscriptions'
import useStudentGlobalPusherSubscriptions from 'hooks/Pusher/useStudentGlobalPusherSubscriptions'
import { isLocalHost } from '../utils'
import {
  studentSectionTransferCallback,
  closeCourseSection,
  updateUnreadNotificationsCallback
} from '../utils/users'
import ModalContainer from './Modal'
import Routes from 'routes/index'
import LoomProvider from './LoomProvider'
import { getUnsavedWorkMessage, willLoseUnsavedWork } from '../utils/unsavedwork'
import { LexiconContext } from '../contexts/Lexicon'
import { ThemeProvider } from 'styled-components'
import { light, dark } from '../styles/Themes/theme'
import useSystemTheme from 'react-use-system-theme'

export const App = ({
  dispatch,
  children,
  history,
  user,
  orgId,
  appUpdateAvailable,
  isChangingOrganization,
  isAuthenticated,
  userIsLoggedInFetchingAllData,
  displayModal,
  courses,
  pastCourses,
  manifest,
  router,
  unsavedWork,
  themeSelection
}) => {
  const lexicon = useContext(LexiconContext)
  const systemTheme = useSystemTheme()
  const isLoggedInAndRedirected = useSelector(
    state => state.auth.isLoggedInAndRedirected
  )
  const isMentor = useSelector(state => state.auth.isMentor)
  const isGuardian = useSelector(state => state.auth.user?.guardian_user)

  const [collapsed, setCollapsed] = useState(true)
  const [theme, setTheme] = useState(themeSelection)

  const toggleCollapse = () => {
    setCollapsed(!collapsed)
  }

  useEffect(() => {
    if (themeSelection === 'system') {
      setTheme(systemTheme)
    } else {
      setTheme(themeSelection)
    }
  }, [themeSelection, systemTheme])

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      window.location &&
      window.location.protocol === 'http:' &&
      !isLocalHost()
    ) {
      window.location.href = window.location.href.replace(/^http(?!s)/, 'https')
    }

    // used to determine if this is a hard refresh/load in MD container
    window.oeQwAppLoading = true

    // TODO: look at handling this elsewhere, not a fan of this
    if (children === null) {
      dispatch(push('/login'))
    }

    let action = setUserNeedsAppUpdate
    let channel = window.oeQwPusherInstance.subscribe('questweb')

    channel.bind('update', function () {
      dispatch(action())
    })

    return () => {
      window.oeQwPusherInstance.unsubscribe('questweb')
      if (user) {
        window.oeQwPusherInstance.subscribe(
          `quest.organizations.${orgId}.users`
        )
        window.oeQwPusherInstance.unsubscribe(
          `quest.organizations.${orgId}.mentors`
        )
        window.oeQwPusherInstance.unsubscribe(`quest.users.${user.id}`)
      }
    }
  }, [])

  useEffect(() => {
    if (orgId) {
      lexicon.dispatch({
        type: 'SET_LEXICON',
        orgId
      })
    }
  }, [orgId])

  useEffect(() => {
    if (user && orgId) {
      let orgUsersChannel = window.oeQwPusherInstance.subscribe(
        `quest.organizations.${orgId}.users`
      )
      orgUsersChannel.bind(
        'user_course_enrollments_changed',
        studentSectionTransferCallback,
        {
          dispatch,
          user
        }
      )
      return () => {
        orgUsersChannel.unbind('user_course_enrollments_changed')
      }
    }
  }, [user, orgId, dispatch])

  useEffect(() => {
    if (user && orgId) {
      let orgUsersChannel = window.oeQwPusherInstance.subscribe(
        `quest.organizations.${orgId}.users`
      )
      orgUsersChannel.bind('course_section_closed', closeCourseSection, {
        dispatch,
        user,
        courses,
        isMentor,
        pastCourses,
        manifest
      })

      orgUsersChannel.bind(
        'student_master_course_notifications_updated',
        updateUnreadNotificationsCallback,
        {
          dispatch,
          user
        }
      )

      return () => {
        orgUsersChannel.unbind('course_section_closed')
        orgUsersChannel.unbind('student_master_course_notifications_updated')
      }
    }
  }, [user, orgId, dispatch, user, courses, pastCourses, manifest])

  const canLoseWork = willLoseUnsavedWork({ unsavedWork })

  useEffect(() => {
    if (canLoseWork) {
      const onBeforeUnload = e => {
        e.preventDefault()
        return e.returnValue = getUnsavedWorkMessage()
      }
      window.addEventListener('beforeunload', onBeforeUnload)

      return () => {
        window.removeEventListener('beforeunload', onBeforeUnload)
      }
    }
  }, [canLoseWork])

  const warnUnsavedWork = (location, action) => {
    if (
      !location.pathname.startsWith('/login') && // logout is handled separately
      !location.pathname.startsWith('/loading') && // org switch is handled separately
      willLoseUnsavedWork({
        unsavedWork,
        newPath: location.pathname,
        oldPath: router.location.pathname
      })
    ) {
      return getUnsavedWorkMessage()
    } else {
      return true
    }
  }

  useMentorGlobalPusherSubscriptions(
    user,
    orgId,
    isMentor,
    courses,
    pastCourses
  )

  useStudentGlobalPusherSubscriptions(user, orgId, isMentor)

  useConfigureRollbar(user)

  const shouldDisplaySideNavAndFooter = () => {
    const isGuardianLobbyRoute = router.location.pathname === '/guardianlobby'
    const isPoliciesRoute = router.location.pathname === '/policies'

    return (
      !isPoliciesRoute &&
      !isChangingOrganization &&
      !userIsLoggedInFetchingAllData &&
      !isGuardianLobbyRoute &&
      !isGuardianLobbyRoute
    )
  }

  const displayNavAndFooter = shouldDisplaySideNavAndFooter()

  const selectedTheme = !isLoggedInAndRedirected || isMentor || isGuardian
    ? light
    : theme === 'light'
      ? light
      : dark

  return (
    <ThemeProvider theme={selectedTheme}>
      <LoomProvider>
        <div className={ `${isAuthenticated ? `${selectedTheme.style}` : ''}` }>
          {displayModal && <ModalContainer />}

          {displayNavAndFooter && <SideNav collapsed={collapsed} toggleCollapse={toggleCollapse}/>}

          {isAuthenticated && displayNavAndFooter && !isMentor && (
              <Announcements isSideNavCollapsed={collapsed}/>
          )}

          {appUpdateAvailable && <RefreshApp dispatch={dispatch} />}

          {!isChangingOrganization && !userIsLoggedInFetchingAllData ? (
            <main className={ `${isAuthenticated ? `${selectedTheme.style}` : ''} ${appUpdateAvailable ? ' refresh-app' :'' }` }>
              <Routes history={history} />
            </main>
          ) : (
            <div className='loading'/>
          )}

          {displayNavAndFooter && <Footer />}

          <Prompt
            when={canLoseWork}
            message={warnUnsavedWork}
          />
        </div>
      </LoomProvider>
    </ThemeProvider>
  )
}

App.propTypes = {
  actions: PropTypes.object,
  children: PropTypes.element,
  dispatch: PropTypes.func,
  history: PropTypes.object,
  isAuthenticated: PropTypes.bool,
  orgId: PropTypes.number,
  courses: PropTypes.array,
  pastCourses: PropTypes.array,
  user: PropTypes.object,
  appUpdateAvailable: PropTypes.bool,
  isChangingOrganization: PropTypes.bool,
  userIsLoggedInFetchingAllData: PropTypes.bool,
  displayModal: PropTypes.bool,
  manifest: PropTypes.object,
  router: PropTypes.object.isRequired,
  unsavedWork: PropTypes.bool
}

export function mapStateToProps(state) {
  return {
    displayModal: state._modal.modalType !== null,
    isAuthenticated: state.auth.isAuthenticated,
    orgId: state.organization.orgId,
    user: state.auth.user,
    userIsLoggedInFetchingAllData: state.auth.userIsLoggedInFetchingAllData,
    appUpdateAvailable: state.appupdate.appUpdateAvailable,
    isChangingOrganization: state.organization.isChangingOrganization,
    courses: state.usercourses.currentUserCourses,
    pastCourses: state.usercourses.pastUserCourses,
    manifest: state.manifest,
    router: state.router,
    unsavedWork: state.unsavedwork.items,
    themeSelection: state.auth.theme
  }
}

export default connect(mapStateToProps)(App)
